/**
 * Estado de diagnostico del puente Raspberry.
 *
 * Existe para que el aviso al guardia no sea ruido: un consorcio sin Raspberry recibe
 * `vectorizations: []` y no necesita la extension para nada, asi que mostrarle un warning
 * permanente seria molestarlo por algo que no le aplica. El aviso se prende SOLO despues de
 * que un addEvent real trajo targets y el envio fallo.
 *
 * Singleton de modulo, mismo patron que src/views/Reservations/store/reservationScanStore.ts.
 * Vive en memoria a proposito: es estado de sesion, no algo que deba sobrevivir un reload.
 */
import { RaspberryErrorCode, VectorizeRequest } from "./raspberryBridge";

/** Ultimo target visto, para que "Probar conexion" tenga contra que probar. */
export type RaspberryTarget = Pick<VectorizeRequest, "ip" | "port" | "path" | "token">;

export interface RaspberryDiagnosticsState {
  /** true si hubo al menos un fallo real. Es lo que prende el aviso en el menu. */
  hasIssue: boolean;
  lastErrorCode: RaspberryErrorCode | null;
  lastError: string | null;
  lastTarget: RaspberryTarget | null;
  lastSuccessAt: number | null;
  lastFailureAt: number | null;
  /** Cuantos addEvent trajeron targets en esta sesion. 0 => el edificio no usa Raspberry. */
  targetsSeen: number;
}

const INITIAL_STATE: RaspberryDiagnosticsState = {
  hasIssue: false,
  lastErrorCode: null,
  lastError: null,
  lastTarget: null,
  lastSuccessAt: null,
  lastFailureAt: null,
  targetsSeen: 0,
};

let state: RaspberryDiagnosticsState = INITIAL_STATE;

const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((listener) => listener());
}

export function subscribeRaspberryDiagnostics(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getRaspberryDiagnostics(): RaspberryDiagnosticsState {
  return state;
}

/** Se llama cuando un addEvent trajo targets, antes de intentar el envio. */
export function recordRaspberryTarget(target: RaspberryTarget): void {
  state = { ...state, lastTarget: target, targetsSeen: state.targetsSeen + 1 };
  emit();
}

export function recordRaspberrySuccess(): void {
  state = {
    ...state,
    hasIssue: false,
    lastErrorCode: null,
    lastError: null,
    lastSuccessAt: Date.now(),
  };
  emit();
}

export function recordRaspberryFailure(errorCode: RaspberryErrorCode, error: string): void {
  state = {
    ...state,
    hasIssue: true,
    lastErrorCode: errorCode,
    lastError: error,
    lastFailureAt: Date.now(),
  };
  emit();
}

/** Solo para tests: el store es un singleton de modulo y persiste entre casos. */
export function resetRaspberryDiagnostics(): void {
  state = INITIAL_STATE;
  emit();
}
